import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Form,
  Button,
  Space,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Table,
  Row,
  Col,
  Typography,
  App,
  Spin,
  Tag,
  Alert,
  Divider,
  Checkbox,
  Radio,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, SaveOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  usePaymentVoucher,
  useCreatePaymentVoucher,
  useUpdatePaymentVoucher,
  usePostPaymentVoucher,
  useApSettlementContext,
  useApSettlementContextMulti,
} from '@/hooks/usePaymentVoucher';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { InvoiceNumberWithNarration } from '@/components/finance/InvoiceNumberWithNarration';
import { DOCUMENT_ENTITY_TYPE_PAYMENT_VOUCHER } from '@/constants/document-entity-types';
import paymentVoucherService from '@/services/paymentVoucher.service';
import type {
  CreatePaymentVoucherPayload,
  PaymentType,
  PaymentMode,
  SettlementLineInput,
  ApSettlementContext,
  PaymentSettlementScope,
} from '@/services/paymentVoucher.service';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import { supplierAdvanceRequestService } from '@/services/supplierAdvanceRequest.service';
import { supplierInvoiceService } from '@/services/supplierInvoice.service';
import directPurchaseInvoiceService from '@/services/directPurchaseInvoice.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { convertToBaseCurrency } from '@/utils/currencyConversion.util';
import { useProjectsForSelection } from '@/hooks/useProject';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useApplyUserDefaultDepartment } from '@/utils/userDefaultDepartment';

const { Title } = Typography;
const { TextArea } = Input;

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

const TRANSFER_TYPES: PaymentType[] = ['BankTransfer', 'CashTransfer', 'AccountTransfer'];

type SettlementRow = {
  supplier_invoice_id: string;
  supplier_id?: string;
  supplier_name?: string;
  invoice_number: string;
  narration?: string;
  invoice_type?: string;
  due_date: string;
  grand_total: number;
  balance_due: number;
  days_overdue: number;
  payment_status?: string;
  cash_amount: number;
  selected: boolean;
};

type SarSettlementRow = {
  supplier_advance_request_id: string;
  advance_request_number: string;
  po_number: string | null;
  remaining_amount: number;
  requested_amount: number;
  status: string;
  allocated_amount: number;
  selected: boolean;
};

// Stable fallback: a fresh `[]` default would retrigger the SAR rows effect on every render.
const NO_PAYABLE_SARS: Awaited<ReturnType<typeof supplierAdvanceRequestService.listPayableForPayment>> = [];

const PaymentVoucherFormPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const paymentTypes = useMemo(
    () => [
      { value: 'SupplierBillPayment' as PaymentType, label: t('paymentTypeSupplierBillPayment') },
      { value: 'SupplierAdvance' as PaymentType, label: t('paymentTypeSupplierAdvance') },
      { value: 'EmployeePayment' as PaymentType, label: t('paymentTypeEmployeePayment') },
      { value: 'ExpensePayment' as PaymentType, label: t('paymentTypeExpensePayment') },
      { value: 'DirectPayment' as PaymentType, label: t('paymentTypeDirectPayment') },
      { value: 'BankTransfer' as PaymentType, label: t('paymentTypeBankTransfer') },
      { value: 'CashTransfer' as PaymentType, label: t('paymentTypeCashTransfer') },
      { value: 'AccountTransfer' as PaymentType, label: t('paymentTypeAccountTransfer') },
    ],
    [t]
  );
  const paymentModes = useMemo(
    () => [
      { value: 'Cash' as PaymentMode, label: t('modeCash') },
      { value: 'Cheque' as PaymentMode, label: t('modeCheque') },
      { value: 'BankTransfer' as PaymentMode, label: t('modeBankTransfer') },
      { value: 'Online' as PaymentMode, label: t('modeOnline') },
      { value: 'Card' as PaymentMode, label: t('modeCard') },
      { value: 'PDC' as PaymentMode, label: t('modePdc') },
      { value: 'Other' as PaymentMode, label: t('modeOther') },
    ],
    [t]
  );
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const advanceRequestId = searchParams.get('advanceRequestId');
  const supplierInvoiceIdFromUrl = searchParams.get('supplierInvoiceId')?.trim() || undefined;
  const navigate = useNavigate();
  const { message } = App.useApp();
  const isNew = id === 'new' || !id;
  const isEdit = id && !isNew;

  const [form] = Form.useForm();
  useApplyUserDefaultDepartment(form, isNew);
  const [settlementRows, setSettlementRows] = useState<SettlementRow[]>([]);
  const [multiSupplierIds, setMultiSupplierIds] = useState<string[]>([]);
  const [sarSettlementRows, setSarSettlementRows] = useState<SarSettlementRow[]>([]);
  const [advanceAdj, setAdvanceAdj] = useState<Record<string, number>>({});
  const [dnAdj, setDnAdj] = useState<Record<string, number>>({});
  const [directLines, setDirectLines] = useState<Array<{ account_id: string; amount: number; description?: string; project_id?: string }>>([]);
  const [chargeLines, setChargeLines] = useState<Array<{ account_id: string; amount: number; description?: string; project_id?: string }>>([]);

  const { baseCurrency, allCurrencies, loading: currenciesLoading } = useCurrency();
  const currencyOptions = useMemo(
    () =>
      allCurrencies.map((c) => ({
        value: c.currency_code,
        label: `${c.currency_code} - ${c.currency_name}`,
      })),
    [allCurrencies]
  );

  const paymentType = Form.useWatch('payment_type', form) as PaymentType | undefined;
  const settlementScope = (Form.useWatch('settlement_scope', form) as PaymentSettlementScope | undefined) ?? 'SingleSupplier';
  const isMultiSupplier =
    paymentType === 'SupplierBillPayment' && settlementScope === 'MultiSupplier';
  const supplierId = Form.useWatch('supplier_id', form);
  const paymentMode = Form.useWatch('payment_mode', form) as PaymentMode | undefined;
  const amount = Form.useWatch('amount', form);
  const currency = Form.useWatch('currency', form) as string | undefined;
  const exchangeRate = Form.useWatch('exchange_rate', form);
  const baseCode = baseCurrency?.currency_code ?? 'AED';
  const isForeignCurrency = !!currency && currency !== baseCode;
  const baseAmountPreview = useMemo(() => {
    if (!amount || !isForeignCurrency) return Number(amount) || 0;
    const rate = Number(exchangeRate);
    if (Number.isFinite(rate) && rate > 0) {
      return Math.round(Number(amount) * rate * 100) / 100;
    }
    return convertToBaseCurrency(Number(amount) || 0, currency, allCurrencies, baseCode);
  }, [amount, currency, exchangeRate, isForeignCurrency, allCurrencies, baseCode]);

  const { data: pv, isLoading } = usePaymentVoucher(isEdit ? id : undefined);
  const createMutation = useCreatePaymentVoucher();
  const updateMutation = useUpdatePaymentVoucher();
  const postMutation = usePostPaymentVoucher();
  const { data: apContext, isLoading: apContextLoading } = useApSettlementContext(
    paymentType === 'SupplierBillPayment' && !isMultiSupplier ? supplierId : undefined,
    paymentType === 'SupplierBillPayment' && !isMultiSupplier ? currency : undefined
  );
  const { data: apContextMulti, isLoading: apContextMultiLoading } = useApSettlementContextMulti(
    isMultiSupplier ? multiSupplierIds : undefined,
    isMultiSupplier ? currency : undefined
  );

  const { data: payableSars = NO_PAYABLE_SARS, isLoading: payableSarsLoading } = useQuery({
    queryKey: ['sar-payable-for-payment', supplierId, currency],
    queryFn: () => supplierAdvanceRequestService.listPayableForPayment(String(supplierId), String(currency)),
    enabled: paymentType === 'SupplierAdvance' && !!supplierId && !!currency,
  });

  const { data: employeePayables = [], isLoading: employeePayablesLoading } = useQuery({
    queryKey: ['pv-employee-payables'],
    queryFn: () => paymentVoucherService.listEmployeePayables(),
    enabled: paymentType === 'EmployeePayment',
  });

  const { data: assetAccounts = [] } = useChartAccountsByType('Asset', false);
  const { data: liabilityAccounts = [] } = useChartAccountsByType('Liability', false);
  const { data: expenseAccounts = [] } = useChartAccountsByType('Expense', false);
  const { data: projects = [] } = useProjectsForSelection();
  const projectOptions = useMemo(
    () =>
      (projects as any[]).map((p) => ({
        value: p.id,
        label: p.project_number ? `${p.project_number} - ${p.project_name}` : p.project_name ?? p.id,
      })),
    [projects]
  );

  const { data: allAccounts = [] } = useQuery({
    queryKey: ['chart-of-accounts-all'],
    queryFn: async () => {
      const res = await api.get('/finance/chart-of-accounts');
      const list = unwrap<any[]>(res) ?? [];
      return Array.isArray(list) ? list.filter((a: any) => !a.is_group) : [];
    },
  });

  const { data: suppliersRaw = [] } = useQuery({
    queryKey: ['procurement-suppliers-list'],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers');
      return unwrap<any[]>(res) ?? [];
    },
  });
  const supplierOptions = useMemo(
    () => (Array.isArray(suppliersRaw) ? suppliersRaw.map((s: any) => ({ value: s.id, label: s.supplier_name ?? s.id })) : []),
    [suppliersRaw]
  );

  const paymentAccountOptions = useMemo(
    () => assetAccounts.map((a) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` })),
    [assetAccounts]
  );
  const destinationAccountOptions = paymentAccountOptions;
  const advanceGlAccountOptions = useMemo(() => {
    const byId = new Map<string, { value: string; label: string }>();
    for (const a of [...liabilityAccounts, ...assetAccounts]) {
      if (!byId.has(a.id)) {
        byId.set(a.id, { value: a.id, label: `${a.account_code} - ${a.account_name}` });
      }
    }
    return Array.from(byId.values()).sort((x, y) => x.label.localeCompare(y.label));
  }, [liabilityAccounts, assetAccounts]);
  const directLineAccountOptions = useMemo(
    () => allAccounts.map((a: any) => ({ value: a.id, label: `${a.account_code} - ${a.account_name}` })),
    [allAccounts]
  );

  const supplierNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of supplierOptions) m.set(String(o.value), String(o.label));
    return m;
  }, [supplierOptions]);

  // Must stay above the populate effect: with cached context data both run on mount, and the populate must win.
  useEffect(() => {
    if (paymentType === 'SupplierBillPayment' && currency) {
      setSettlementRows([]);
      setAdvanceAdj({});
      setDnAdj({});
      if (isMultiSupplier) {
        setMultiSupplierIds([]);
      }
    }
  }, [currency, paymentType, isMultiSupplier]);

  useEffect(() => {
    if (paymentType !== 'SupplierBillPayment') {
      setSettlementRows([]);
      return;
    }
    if (isMultiSupplier) {
      if (!apContextMulti?.invoices) return;
      setSettlementRows((prev) => {
        const prevById = new Map(prev.map((r) => [r.supplier_invoice_id, r]));
        return apContextMulti.invoices.map((inv) => {
          const prevRow = prevById.get(inv.id);
          const sid = inv.supplier_id ?? '';
          return {
            supplier_invoice_id: inv.id,
            supplier_id: sid,
            supplier_name: sid ? supplierNameById.get(sid) : undefined,
            invoice_number: inv.invoice_number,
            narration: inv.narration,
            invoice_type: inv.invoice_type,
            due_date: inv.due_date ?? '',
            grand_total: Number(inv.grand_total),
            balance_due: Number(inv.balance_due),
            days_overdue: Number(inv.days_overdue ?? 0),
            payment_status: inv.payment_status,
            cash_amount: prevRow?.cash_amount ?? 0,
            selected: prevRow?.selected ?? false,
          };
        });
      });
      setAdvanceAdj({});
      setDnAdj({});
      return;
    }
    if (apContext?.invoices?.length) {
      setSettlementRows(
        apContext.invoices.map((inv) => ({
          supplier_invoice_id: inv.id,
          supplier_id: inv.supplier_id,
          invoice_number: inv.invoice_number,
          narration: inv.narration,
          invoice_type: (inv as { invoice_type?: string }).invoice_type,
          due_date: inv.due_date ?? '',
          grand_total: Number(inv.grand_total),
          balance_due: Number(inv.balance_due),
          days_overdue: Number(inv.days_overdue ?? 0),
          payment_status: inv.payment_status,
          cash_amount: supplierInvoiceIdFromUrl === inv.id ? Number(inv.balance_due) : 0,
          selected: supplierInvoiceIdFromUrl === inv.id,
        }))
      );
      setAdvanceAdj({});
      setDnAdj({});
    }
  }, [
    paymentType,
    isMultiSupplier,
    apContext,
    apContextMulti,
    supplierInvoiceIdFromUrl,
    supplierNameById,
  ]);

  useEffect(() => {
    if (!currency || currency === baseCode) {
      form.setFieldValue('exchange_rate', 1);
      return;
    }
    const row = allCurrencies.find((c) => c.currency_code === currency);
    if (row?.exchange_rate) {
      form.setFieldValue('exchange_rate', Number(row.exchange_rate));
    }
  }, [currency, baseCode, allCurrencies, form]);

  useEffect(() => {
    if (isMultiSupplier) return;
    if (!supplierId && paymentType === 'SupplierBillPayment') {
      setSettlementRows([]);
      setAdvanceAdj({});
      setDnAdj({});
    }
  }, [supplierId, paymentType, isMultiSupplier]);

  useEffect(() => {
    if (paymentType === 'SupplierBillPayment' && !isMultiSupplier) {
      setMultiSupplierIds([]);
    }
  }, [paymentType, isMultiSupplier]);

  const settlementSummary = useMemo(() => {
    const selected = settlementRows.filter((r) => r.selected || r.cash_amount > 0);
    const totalCash = selected.reduce((s, r) => s + r.cash_amount, 0);
    const totalAdv = Object.values(advanceAdj).reduce((s, v) => s + Number(v || 0), 0);
    const totalDn = Object.values(dnAdj).reduce((s, v) => s + Number(v || 0), 0);
    const discount = Number(form.getFieldValue('discount_amount') ?? 0);
    const writeOff = Number(form.getFieldValue('write_off_amount') ?? 0);
    return {
      totalCash,
      totalAdv,
      totalDn,
      discount,
      writeOff,
      netPayable: Math.max(0, totalCash + totalAdv + totalDn - discount - writeOff),
    };
  }, [settlementRows, advanceAdj, dnAdj, form, amount]);

  useEffect(() => {
    if (paymentType === 'SupplierBillPayment' && settlementSummary.totalCash > 0) {
      form.setFieldValue('amount', settlementSummary.totalCash);
    }
  }, [paymentType, settlementSummary.totalCash, form]);

  const sarSettlementTotal = useMemo(
    () =>
      sarSettlementRows
        .filter((r) => r.selected || r.allocated_amount > 0)
        .reduce((s, r) => s + Number(r.allocated_amount || 0), 0),
    [sarSettlementRows]
  );

  useEffect(() => {
    if (paymentType === 'SupplierAdvance' && sarSettlementTotal > 0) {
      form.setFieldValue('amount', Math.round(sarSettlementTotal * 100) / 100);
    }
  }, [paymentType, sarSettlementTotal, form]);

  useEffect(() => {
    if (paymentType !== 'SupplierAdvance' || !supplierId || !currency) {
      if (paymentType !== 'SupplierAdvance') setSarSettlementRows((prev) => (prev.length ? [] : prev));
      return;
    }
    const prevById = new Map(sarSettlementRows.map((r) => [r.supplier_advance_request_id, r]));
    const next: SarSettlementRow[] = (payableSars ?? []).map((s) => {
      const prev = prevById.get(s.id);
      const preselect = Boolean(advanceRequestId && s.id === advanceRequestId);
      const selected = prev?.selected ?? preselect;
      const allocated =
        prev != null
          ? prev.allocated_amount
          : preselect
            ? Number(s.remaining_amount)
            : 0;
      return {
        supplier_advance_request_id: s.id,
        advance_request_number: s.advance_request_number,
        po_number: s.po_number,
        remaining_amount: Number(s.remaining_amount),
        requested_amount: Number(s.requested_amount),
        status: s.status,
        allocated_amount: selected ? Math.min(allocated || Number(s.remaining_amount), Number(s.remaining_amount)) : 0,
        selected,
      };
    });
    // Keep edit-time lines that may already be fully paid (not in payable list)
    for (const prev of sarSettlementRows) {
      if (!next.some((n) => n.supplier_advance_request_id === prev.supplier_advance_request_id) && prev.selected) {
        next.push(prev);
      }
    }
    setSarSettlementRows(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuild from payable list; previous selection merged via prevById
  }, [paymentType, supplierId, currency, payableSars, advanceRequestId]);

  useEffect(() => {
    if (!isNew) return;
    form.resetFields();
    form.setFieldsValue({
      voucher_date: dayjs(),
      // Never seed AED when paying a SAR — currency comes from the advance/PO.
      currency: advanceRequestId ? undefined : (baseCurrency?.currency_code ?? 'AED'),
      payment_type: advanceRequestId ? 'SupplierAdvance' : 'SupplierBillPayment',
      settlement_scope: 'SingleSupplier',
      payment_mode: 'Cash',
    });
    setSettlementRows([]);
    setMultiSupplierIds([]);
    setSarSettlementRows([]);
    setAdvanceAdj({});
    setDnAdj({});
    setDirectLines([]);
    setChargeLines([]);
  }, [isNew, id, form, advanceRequestId, baseCurrency?.currency_code]);

  useEffect(() => {
    if (!isNew || !advanceRequestId) return;
    let cancelled = false;
    supplierAdvanceRequestService.getById(advanceRequestId).then((sar) => {
      if (cancelled) return;
      const ccy = String(sar.currency || '').trim().toUpperCase();
      form.setFieldsValue({
        payment_type: 'SupplierAdvance',
        supplier_id: sar.supplier_id,
        currency: ccy || undefined,
        narration: `Payment for ${sar.advance_request_number}`,
        department_id: sar.department_id || undefined,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isNew, advanceRequestId, form]);

  useEffect(() => {
    if (!isNew || !supplierInvoiceIdFromUrl) return;
    let cancelled = false;
    supplierInvoiceService
      .getById(supplierInvoiceIdFromUrl)
      .catch(() => directPurchaseInvoiceService.getById(supplierInvoiceIdFromUrl) as Promise<any>)
      .then((inv: any) => {
        if (cancelled || !inv) return;
        form.setFieldsValue({
          payment_type: 'SupplierBillPayment',
          settlement_scope: 'SingleSupplier',
          supplier_id: inv.supplier_id,
          currency: String(inv.currency || '').trim().toUpperCase() || undefined,
          narration: `Payment for ${inv.invoice_number}`,
          department_id: inv.department_id || undefined,
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isNew, supplierInvoiceIdFromUrl, form]);

  useEffect(() => {
    if (pv && isEdit) {
      const editScope =
        (pv.settlement_scope as PaymentSettlementScope) ||
        (!pv.supplier_id && pv.payment_type === 'SupplierBillPayment' ? 'MultiSupplier' : 'SingleSupplier');
      form.setFieldsValue({
        voucher_date: pv.voucher_date ? dayjs(pv.voucher_date) : undefined,
        payment_type: pv.payment_type,
        settlement_scope: editScope,
        supplier_id: pv.supplier_id,
        employee_id: pv.employee_id,
        leave_settlement_id: pv.leave_settlement_id,
        payee_name: pv.payee_name,
        payment_account_id: pv.payment_account_id,
        destination_account_id: pv.destination_account_id,
        amount: pv.amount,
        currency: pv.currency ?? 'AED',
        payment_mode: pv.payment_mode,
        reference_number: pv.reference_number,
        narration: pv.narration,
        cheque_number: pv.cheque_number,
        cheque_date: pv.cheque_date ? dayjs(pv.cheque_date) : undefined,
        bank_name: pv.bank_name,
        transaction_reference: pv.transaction_reference,
        department_id: pv.department_id || undefined,
      });
      form.setFieldsValue({
        discount_amount: Number((pv as any).discount_amount ?? 0),
        write_off_amount: Number((pv as any).write_off_amount ?? 0),
        settlement_remarks: (pv as any).settlement_remarks,
      });
      const snapshot = (pv as any).settlement_snapshot as SettlementLineInput[] | undefined;
      if (editScope === 'MultiSupplier') {
        const ids = [
          ...new Set(
            (pv.allocations ?? [])
              .map((a) => a.supplier_id || a.supplierInvoice?.supplier_id)
              .filter(Boolean) as string[]
          ),
        ];
        setMultiSupplierIds(ids);
        if (snapshot?.length && ids.length) {
          paymentVoucherService
            .getApSettlementContextMulti(ids, pv.currency ?? 'AED')
            .then((ctx) => {
              const byInv = new Map(ctx.invoices.map((i) => [i.id, i]));
              setSettlementRows(
                snapshot.map((line) => {
                  const inv = byInv.get(line.supplier_invoice_id);
                  const sid = inv?.supplier_id ?? '';
                  return {
                    supplier_invoice_id: line.supplier_invoice_id,
                    supplier_id: sid,
                    supplier_name: sid ? supplierNameById.get(sid) : undefined,
                    invoice_number: inv?.invoice_number ?? line.supplier_invoice_id,
                    narration: inv?.narration,
                    due_date: inv?.due_date ?? '',
                    grand_total: Number(inv?.grand_total ?? 0),
                    balance_due: Number(inv?.balance_due ?? 0),
                    days_overdue: Number(inv?.days_overdue ?? 0),
                    payment_status: inv?.payment_status,
                    cash_amount: Number(line.cash_amount ?? 0),
                    selected: Number(line.cash_amount) > 0,
                  };
                })
              );
            })
            .catch(() => undefined);
        }
      } else if (snapshot?.length && pv.supplier_id) {
        paymentVoucherService.getApSettlementContext(pv.supplier_id, pv.currency ?? 'AED').then((ctx: ApSettlementContext) => {
          const byInv = new Map(ctx.invoices.map((i) => [i.id, i]));
          setSettlementRows(
            snapshot.map((line) => {
              const inv = byInv.get(line.supplier_invoice_id);
              return {
                supplier_invoice_id: line.supplier_invoice_id,
                invoice_number: inv?.invoice_number ?? line.supplier_invoice_id,
                narration: inv?.narration,
                due_date: inv?.due_date ?? '',
                grand_total: Number(inv?.grand_total ?? 0),
                balance_due: Number(inv?.balance_due ?? 0),
                days_overdue: Number(inv?.days_overdue ?? 0),
                payment_status: inv?.payment_status,
                cash_amount: Number(line.cash_amount ?? 0),
                selected: Number(line.cash_amount) > 0,
              };
            })
          );
          const adv: Record<string, number> = {};
          const dn: Record<string, number> = {};
          for (const line of snapshot) {
            for (const a of line.advance_adjustments ?? []) {
              adv[`${line.supplier_invoice_id}:${a.payment_voucher_id}`] = Number(a.amount);
            }
            for (const d of line.debit_note_adjustments ?? []) {
              dn[`${line.supplier_invoice_id}:${d.supplier_debit_note_id}`] = Number(d.amount);
            }
          }
          setAdvanceAdj(adv);
          setDnAdj(dn);
        });
      } else if (pv.allocations?.length) {
        setSettlementRows(
          pv.allocations.map((a: any) => ({
            supplier_invoice_id: a.supplier_invoice_id,
            invoice_number: a.supplierInvoice?.invoice_number ?? a.supplier_invoice_id,
            due_date: '',
            grand_total: 0,
            balance_due: 0,
            days_overdue: 0,
            cash_amount: Number(a.allocated_amount),
            selected: Number(a.allocated_amount) > 0,
          }))
        );
      }
      if (pv.directLines?.length) {
        setDirectLines(
          pv.directLines.map((l: any) => ({
            account_id: l.account_id,
            amount: Number(l.amount),
            description: l.description,
            project_id: l.project_id,
          }))
        );
      }
      if (pv.chargeLines?.length) {
        setChargeLines(
          pv.chargeLines.map((l: any) => ({
            account_id: l.account_id,
            amount: Number(l.amount),
            description: l.description,
            project_id: l.project_id,
          }))
        );
      }
      if (pv.payment_type === 'SupplierAdvance' && pv.advanceAllocations?.length) {
        setSarSettlementRows(
          pv.advanceAllocations.map((a) => ({
            supplier_advance_request_id: a.supplier_advance_request_id,
            advance_request_number:
              a.supplierAdvanceRequest?.advance_request_number ?? a.supplier_advance_request_id,
            po_number: null,
            remaining_amount: Number(a.allocated_amount),
            requested_amount: Number(a.allocated_amount),
            status: '',
            allocated_amount: Number(a.allocated_amount),
            selected: Number(a.allocated_amount) > 0,
          }))
        );
      }
    }
  }, [pv, isEdit, form, supplierNameById]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const scope: PaymentSettlementScope =
        values.payment_type === 'SupplierBillPayment'
          ? values.settlement_scope === 'MultiSupplier'
            ? 'MultiSupplier'
            : 'SingleSupplier'
          : 'SingleSupplier';
      const payload: CreatePaymentVoucherPayload = {
        voucher_date: values.voucher_date ? dayjs(values.voucher_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        payment_type: values.payment_type,
        settlement_scope: values.payment_type === 'SupplierBillPayment' ? scope : undefined,
        supplier_id: scope === 'MultiSupplier' ? undefined : values.supplier_id,
        employee_id: values.employee_id,
        leave_settlement_id: values.leave_settlement_id,
        payee_name: values.payee_name,
        payment_account_id: values.payment_account_id,
        destination_account_id: values.destination_account_id,
        amount: Number(values.amount),
        currency: values.currency ?? 'AED',
        exchange_rate: Number(values.exchange_rate ?? 1),
        payment_mode: values.payment_mode,
        reference_number: values.reference_number,
        narration: values.narration,
        cheque_number: values.cheque_number,
        cheque_date: values.cheque_date ? dayjs(values.cheque_date).format('YYYY-MM-DD') : undefined,
        bank_name: values.bank_name,
        transaction_reference: values.transaction_reference,
        department_id: values.department_id,
      };

      if (values.payment_type === 'SupplierBillPayment') {
        const lines: SettlementLineInput[] =
          scope === 'MultiSupplier'
            ? settlementRows
                .filter((r) => r.selected || r.cash_amount > 0)
                .map((r) => ({
                  supplier_invoice_id: r.supplier_invoice_id,
                  cash_amount: r.cash_amount,
                }))
            : settlementRows
                .filter(
                  (r) =>
                    r.selected ||
                    r.cash_amount > 0 ||
                    rowAdvanceTotal(r.supplier_invoice_id) > 0 ||
                    rowDnTotal(r.supplier_invoice_id) > 0
                )
                .map((r) => {
                  const advance_adjustments = (apContext?.advances ?? [])
                    .map((adv) => {
                      const key = `${r.supplier_invoice_id}:${adv.id}`;
                      const amt = Number(advanceAdj[key] ?? 0);
                      return amt > 0 ? { payment_voucher_id: adv.id, amount: amt } : null;
                    })
                    .filter(Boolean) as SettlementLineInput['advance_adjustments'];
                  const debit_note_adjustments = (apContext?.debit_notes ?? [])
                    .map((dn) => {
                      const key = `${r.supplier_invoice_id}:${dn.id}`;
                      const amt = Number(dnAdj[key] ?? 0);
                      return amt > 0 ? { supplier_debit_note_id: dn.id, amount: amt } : null;
                    })
                    .filter(Boolean) as SettlementLineInput['debit_note_adjustments'];
                  return {
                    supplier_invoice_id: r.supplier_invoice_id,
                    cash_amount: r.cash_amount,
                    advance_adjustments,
                    debit_note_adjustments,
                  };
                });
        if (!lines.length) {
          message.error(
            scope === 'MultiSupplier'
              ? t('errAddInvoiceCashAllocation')
              : t('errAddInvoiceAllocation')
          );
          return;
        }
        const totalCash = lines.reduce((s, l) => s + Number(l.cash_amount), 0);
        if (Math.abs(totalCash - Number(values.amount)) > 0.01) {
          message.error(t('errPaymentEqualsCash'));
          return;
        }
        payload.settlement_lines = lines;
        payload.discount_amount = Number(values.discount_amount ?? 0);
        payload.write_off_amount = Number(values.write_off_amount ?? 0);
        payload.settlement_remarks = values.settlement_remarks;
        payload.allocations = lines
          .filter((l) => l.cash_amount > 0)
          .map((l) => ({ supplier_invoice_id: l.supplier_invoice_id, allocated_amount: l.cash_amount }));
      } else if (values.payment_type === 'SupplierAdvance') {
        const lines = sarSettlementRows
          .filter((r) => r.selected || r.allocated_amount > 0)
          .map((r) => ({
            supplier_advance_request_id: r.supplier_advance_request_id,
            allocated_amount: Number(r.allocated_amount),
          }))
          .filter((l) => l.allocated_amount > 0);
        if (!lines.length) {
          message.error(t('errSelectAdvanceToPay'));
          return;
        }
        const sum = lines.reduce((s, l) => s + l.allocated_amount, 0);
        if (Math.abs(sum - Number(values.amount)) > 0.01) {
          message.error(t('errPaymentEqualsAdvances'));
          return;
        }
        payload.advance_settlement_lines = lines;
      } else if (values.payment_type === 'DirectPayment' || values.payment_type === 'ExpensePayment') {
        const linesWithAmount = directLines.filter((l) => l.amount > 0);
        const totalDirect = linesWithAmount.reduce((s, l) => s + l.amount, 0);
        if (Math.abs(totalDirect - Number(values.amount)) > 0.01) {
          message.error(t('errDirectLinesEqualAmount'));
          return;
        }
        payload.direct_lines = linesWithAmount.map((l) => ({
          account_id: l.account_id,
          amount: l.amount,
          description: l.description,
          project_id: l.project_id,
        }));
      } else if (values.payment_type === 'EmployeePayment') {
        if (!values.leave_settlement_id) {
          message.error(t('errSelectLeaveSettlement'));
          return;
        }
        payload.leave_settlement_id = values.leave_settlement_id;
        payload.employee_id = values.employee_id;
      }

      const chargeLinesWithAmount = chargeLines.filter((l) => l.amount > 0 && l.account_id);
      if (chargeLinesWithAmount.some((l) => !l.project_id)) {
        message.error(t('errChargeNeedsProject'));
        return;
      }
      if (chargeLinesWithAmount.length) {
        payload.charge_lines = chargeLinesWithAmount.map((l) => ({
          account_id: l.account_id,
          amount: l.amount,
          description: l.description,
          project_id: l.project_id,
        }));
      }

      if (isNew) {
        const created = await createMutation.mutateAsync(payload);
        form.resetFields();
        setSettlementRows([]);
        setSarSettlementRows([]);
        setAdvanceAdj({});
        setDnAdj({});
        setDirectLines([]);
        setChargeLines([]);
        if (created?.id) navigate(`/fin/payment-vouchers/${created.id}`, { replace: true });
        else navigate('/fin/payment-vouchers', { replace: true });
      } else if (id) {
        await updateMutation.mutateAsync({ id, payload });
        form.resetFields();
        setSettlementRows([]);
        setSarSettlementRows([]);
        setAdvanceAdj({});
        setDnAdj({});
        setDirectLines([]);
        setChargeLines([]);
        navigate(`/fin/payment-vouchers/${id}`, { replace: true });
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || t('errSaveFailed'));
    }
  };

  const setCashAmount = (index: number, value: number) => {
    const row = settlementRows[index];
    const v = Math.min(Math.max(0, value), row.balance_due);
    const next = [...settlementRows];
    next[index] = { ...next[index], cash_amount: v, selected: v > 0 };
    setSettlementRows(next);
  };

  const toggleRowSelected = (index: number, checked: boolean) => {
    const next = [...settlementRows];
    const row = next[index];
    next[index] = {
      ...row,
      selected: checked,
      cash_amount: checked ? row.balance_due : 0,
    };
    setSettlementRows(next);
  };

  const payFullOutstanding = (index: number) => {
    setCashAmount(index, settlementRows[index].balance_due);
  };

  const rowAdvanceTotal = (invoiceId: string) =>
    (apContext?.advances ?? []).reduce(
      (s, adv) => s + Number(advanceAdj[`${invoiceId}:${adv.id}`] ?? 0),
      0
    );

  const rowDnTotal = (invoiceId: string) =>
    (apContext?.debit_notes ?? []).reduce(
      (s, dn) => s + Number(dnAdj[`${invoiceId}:${dn.id}`] ?? 0),
      0
    );

  const addDirectLine = () => {
    setDirectLines([...directLines, { account_id: '', amount: 0, project_id: undefined }]);
  };
  const updateDirectLine = (index: number, field: string, value: any) => {
    const next = [...directLines];
    (next[index] as any)[field] = value;
    setDirectLines(next);
  };
  const removeDirectLine = (index: number) => {
    setDirectLines(directLines.filter((_, i) => i !== index));
  };

  const addChargeLine = () => {
    setChargeLines([...chargeLines, { account_id: '', amount: 0, project_id: undefined }]);
  };
  const updateChargeLine = (index: number, field: string, value: any) => {
    const next = [...chargeLines];
    (next[index] as any)[field] = value;
    setChargeLines(next);
  };
  const removeChargeLine = (index: number) => {
    setChargeLines(chargeLines.filter((_, i) => i !== index));
  };

  const chargesTotal = chargeLines.reduce((s, l) => s + Number(l.amount || 0), 0);
  const principalAmount = Number(amount ?? 0);
  const totalBankOutflow = principalAmount + chargesTotal;

  if (isEdit && isLoading && !pv) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const isDraft = (pv as any)?.status === 'Draft';

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/payment-vouchers')}>
            {tc('back')}
          </Button>
          <Title level={4} style={{ margin: '8px 0 0' }}>
            {isNew ? t('newPaymentVoucher') : t('paymentTitleWithNumber', { number: (pv as any)?.voucher_number ?? id })}
          </Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<SaveOutlined />} onClick={handleSave} loading={createMutation.isPending || updateMutation.isPending} disabled={!isDraft && !isNew}>
              {tc('save')}
            </Button>
            {isEdit && isDraft && (
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => postMutation.mutate(id!)} loading={postMutation.isPending}>
                {tc('post')}
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Form form={form} layout="vertical" initialValues={{ voucher_date: dayjs(), payment_type: 'SupplierBillPayment', settlement_scope: 'SingleSupplier', payment_mode: 'Cash' }}>
        <Card title={t('header')} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="voucher_date" label={t('voucherDate')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="department_id"
                label={t('department')}
                rules={[{ required: true, message: t('departmentRequired') }]}
              >
                <DepartmentSelect />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="payment_type" label={t('paymentType')} rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={paymentTypes} />
              </Form.Item>
            </Col>
            {paymentType === 'SupplierBillPayment' && (
              <Col span={8}>
                <Form.Item
                  name="settlement_scope"
                  label={t('settlementScope')}
                  rules={[{ required: true }]}
                  extra={t('settlementScopeExtra')}
                >
                  <Radio.Group
                    options={[
                      { value: 'SingleSupplier', label: t('singleSupplier') },
                      { value: 'MultiSupplier', label: t('multiSupplierPettyCash') },
                    ]}
                    onChange={() => {
                      setSettlementRows([]);
                      setAdvanceAdj({});
                      setDnAdj({});
                      setMultiSupplierIds([]);
                      form.setFieldValue('supplier_id', undefined);
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {(paymentType === 'SupplierAdvance' ||
              (paymentType === 'SupplierBillPayment' && !isMultiSupplier)) && (
              <Col span={8}>
                <Form.Item name="supplier_id" label={t('supplier')} rules={[{ required: true }]}>
                  <Select
                    allowClear
                    placeholder={t('selectSupplier')}
                    showSearch
                    optionFilterProp="label"
                    options={supplierOptions}
                  />
                </Form.Item>
              </Col>
            )}
            {isMultiSupplier && (
              <Col span={16}>
                <Form.Item
                  label={t('suppliers')}
                  required
                  extra={!currency ? t('selectCurrencyFirstThenSuppliers') : undefined}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder={currency ? t('addSuppliers') : t('selectCurrencyFirst')}
                    showSearch
                    optionFilterProp="label"
                    options={supplierOptions}
                    value={multiSupplierIds}
                    disabled={!currency}
                    onChange={(ids: string[]) => {
                      const next = ids || [];
                      const removed = multiSupplierIds.filter((id) => !next.includes(id));
                      setMultiSupplierIds(next);
                      if (removed.length) {
                        setSettlementRows((prev) =>
                          prev.filter((r) => !r.supplier_id || !removed.includes(r.supplier_id))
                        );
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="payee_name"
                  label={t('payeeName')}
                  rules={[
                    {
                      required: paymentMode === 'Cheque' || paymentMode === 'BankTransfer',
                      message: t('payeeNameRequiredCheque'),
                    },
                  ]}
                >
                  <Input placeholder={t('payeeNamePlaceholder')} />
                </Form.Item>
              </Col>
            )}
            {paymentType === 'EmployeePayment' && (
              <Col span={16}>
                <Form.Item
                  name="leave_settlement_id"
                  label={t('leaveSettlementPayable')}
                  rules={[{ required: true, message: t('selectPostedLeaveSettlement') }]}
                  extra={t('leaveSettlementExtra')}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={employeePayablesLoading}
                    placeholder={t('selectLeaveSettlement')}
                    options={(employeePayables as any[]).map((s) => ({
                      value: s.id,
                      label: `${s.settlement_number} — ${s.employee_name || s.employee_code || s.employee_id} — ${Number(s.net_amount).toLocaleString()} (JV ${s.accrual_voucher_number || '—'})`,
                    }))}
                    onChange={(sid) => {
                      const s = (employeePayables as any[]).find((r) => r.id === sid);
                      if (!s) return;
                      form.setFieldsValue({
                        employee_id: s.employee_id,
                        payee_name: s.employee_name || s.employee_code,
                        amount: Number(s.net_amount),
                        department_id: s.department_id || undefined,
                        narration: `Leave settlement ${s.settlement_number} — ${s.employee_name || ''}`,
                      });
                    }}
                  />
                </Form.Item>
                <Form.Item name="employee_id" hidden>
                  <Input />
                </Form.Item>
              </Col>
            )}
            {paymentType &&
              !['SupplierBillPayment', 'SupplierAdvance', 'EmployeePayment'].includes(paymentType) && (
              <Col span={8}>
                <Form.Item name="payee_name" label={t('payeeNameOptional')}>
                  <Input placeholder={t('optional')} />
                </Form.Item>
              </Col>
            )}
            {paymentType === 'EmployeePayment' && (
              <Col span={8}>
                <Form.Item name="payee_name" label={t('employeePayee')}>
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            <Col span={8}>
              <Form.Item
                name="currency"
                label={t('currency')}
                rules={[{ required: true, message: t('selectCurrency') }]}
                tooltip={advanceRequestId ? t('currencyFromAdvanceRequest') : undefined}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={currenciesLoading}
                  options={currencyOptions}
                  disabled={!!advanceRequestId}
                />
              </Form.Item>
            </Col>
            {isForeignCurrency && (
              <Col span={8}>
                <Form.Item
                  name="exchange_rate"
                  label={t('exchangeRateLabel', { from: currency, to: baseCode })}
                  rules={[{ required: true, message: t('enterExchangeRate') }]}
                  initialValue={1}
                >
                  <InputNumber min={0.000001} step={0.0001} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
            <Col span={8}>
              <Form.Item name="amount" label={t('amount')} rules={[{ required: true }]}>
                <InputNumber min={0.01} style={{ width: '100%' }} addonAfter={currency || undefined} />
              </Form.Item>
            </Col>
            {isForeignCurrency && (
              <Col span={24}>
                <Alert
                  type="info"
                  showIcon
                  message={t('glPostingEquivalent', { amount: baseAmountPreview.toLocaleString(), currency: baseCode })}
                />
              </Col>
            )}
            <Col span={8}>
              <Form.Item name="payment_mode" label={t('paymentMode')} rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={paymentModes} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="payment_account_id" label={t('paymentAccountSource')} rules={[{ required: true }]}>
                <Select placeholder={t('selectAccount')} showSearch optionFilterProp="label" options={paymentAccountOptions} />
              </Form.Item>
            </Col>
            {paymentType === 'SupplierAdvance' && (
              <Col span={8}>
                <Form.Item
                  name="destination_account_id"
                  label={t('advanceGlAccount')}
                  rules={[{ required: true, message: t('selectAdvanceGlAccount') }]}
                  tooltip={t('advanceGlTooltip')}
                >
                  <Select
                    placeholder={t('selectGlAccount')}
                    showSearch
                    optionFilterProp="label"
                    options={advanceGlAccountOptions}
                  />
                </Form.Item>
              </Col>
            )}
            {paymentType && TRANSFER_TYPES.includes(paymentType) && (
              <Col span={8}>
                <Form.Item name="destination_account_id" label={t('destinationAccount')} rules={[{ required: true }]}>
                  <Select placeholder={t('selectAccount')} showSearch optionFilterProp="label" options={destinationAccountOptions} />
                </Form.Item>
              </Col>
            )}
            <Col span={8}>
              <Form.Item name="reference_number" label={t('referenceNumber')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="narration" label={t('narration')}>
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="cheque_number" label={t('chequeNumber')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="cheque_date" label={t('chequeDate')}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bank_name" label={t('bankName')}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="transaction_reference" label={t('transactionReference')}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {paymentType === 'EmployeePayment' && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('employeeLeaveSettlementPayment')}
            description={t('employeeLeaveSettlementDesc')}
          />
        )}
        {paymentType === 'EmployeePayment' && !employeePayablesLoading && !(employeePayables as any[]).length && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('noOpenEmployeePayables')}
            description={t('noOpenEmployeePayablesDesc')}
          />
        )}

        {paymentType === 'SupplierAdvance' && supplierId && !currency && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('selectCurrencyBeforeSar')}
          />
        )}

        {paymentType === 'SupplierAdvance' && supplierId && currency && (
          <Card
            title={t('supplierAdvanceRequests')}
            style={{ marginBottom: 16 }}
            loading={payableSarsLoading}
          >
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {t('supplierAdvanceRequestsHint', { currency })}
            </Typography.Paragraph>
            {!payableSarsLoading && !sarSettlementRows.length && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={t('noPayableAdvances', { currency })}
              />
            )}
            <Table
              size="small"
              rowKey="supplier_advance_request_id"
              dataSource={sarSettlementRows}
              pagination={false}
              columns={[
                {
                  title: '',
                  key: 'select',
                  width: 48,
                  render: (_: unknown, record: SarSettlementRow, index: number) => (
                    <Checkbox
                      checked={record.selected || record.allocated_amount > 0}
                      onChange={(e) => {
                        const next = [...sarSettlementRows];
                        const checked = e.target.checked;
                        next[index] = {
                          ...record,
                          selected: checked,
                          allocated_amount: checked
                            ? record.allocated_amount > 0
                              ? record.allocated_amount
                              : record.remaining_amount
                            : 0,
                        };
                        setSarSettlementRows(next);
                      }}
                    />
                  ),
                },
                { title: t('advanceRequest'), dataIndex: 'advance_request_number' },
                { title: t('po'), dataIndex: 'po_number', width: 120, render: (v: string | null) => v || '—' },
                { title: t('status'), dataIndex: 'status', width: 120, render: (v: string) => v || '—' },
                {
                  title: t('remaining'),
                  dataIndex: 'remaining_amount',
                  align: 'right',
                  width: 120,
                  render: (v: number) => `${currency} ${Number(v).toFixed(2)}`,
                },
                {
                  title: t('payAmount'),
                  dataIndex: 'allocated_amount',
                  align: 'right',
                  width: 160,
                  render: (v: number, record: SarSettlementRow, index: number) => (
                    <Space size="small">
                      <InputNumber
                        min={0}
                        max={record.remaining_amount}
                        value={v}
                        onChange={(val) => {
                          const amt = Number(val) || 0;
                          const next = [...sarSettlementRows];
                          next[index] = {
                            ...record,
                            allocated_amount: amt,
                            selected: amt > 0,
                          };
                          setSarSettlementRows(next);
                        }}
                        style={{ width: 110 }}
                      />
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          const next = [...sarSettlementRows];
                          next[index] = {
                            ...record,
                            allocated_amount: record.remaining_amount,
                            selected: true,
                          };
                          setSarSettlementRows(next);
                        }}
                      >
                        {tc('full')}
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              {t('totalAllocated', { currency, amount: sarSettlementTotal.toFixed(2) })}
            </Typography.Text>
          </Card>
        )}

        {paymentType === 'SupplierBillPayment' && !isMultiSupplier && supplierId && !currency && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('selectCurrencyBeforeInvoices')}
          />
        )}

        {isMultiSupplier && !currency && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('selectCurrencyBeforeMulti')}
          />
        )}

        {isMultiSupplier && currency && (
          <>
            <Card
              title={t('multiSupplierAllocation')}
              style={{ marginBottom: 16 }}
              loading={apContextMultiLoading}
            >
              <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                {t('multiSupplierAllocationHint', { currency })}
              </Typography.Paragraph>
              {!multiSupplierIds.length && (
                <Alert type="info" showIcon message={t('addAtLeastOneSupplier')} style={{ marginBottom: 12 }} />
              )}
              {multiSupplierIds.map((sid) => {
                const rows = settlementRows
                  .map((r, index) => ({ r, index }))
                  .filter(({ r }) => r.supplier_id === sid);
                const name = supplierNameById.get(sid) || sid;
                return (
                  <Card
                    key={sid}
                    type="inner"
                    size="small"
                    title={name}
                    style={{ marginBottom: 12 }}
                    extra={
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => {
                          setMultiSupplierIds((prev) => prev.filter((x) => x !== sid));
                          setSettlementRows((prev) => prev.filter((r) => r.supplier_id !== sid));
                        }}
                      >
                        {tc('remove')}
                      </Button>
                    }
                  >
                    {!rows.length && !apContextMultiLoading ? (
                      <Alert type="info" showIcon message={t('noOpenInvoicesForSupplier', { currency })} />
                    ) : (
                      <Table
                        size="small"
                        rowKey="supplier_invoice_id"
                        dataSource={rows.map(({ r }) => r)}
                        pagination={false}
                        columns={[
                          {
                            title: '',
                            key: 'select',
                            width: 48,
                            render: (_: unknown, record: SettlementRow) => {
                              const index = settlementRows.findIndex(
                                (x) => x.supplier_invoice_id === record.supplier_invoice_id
                              );
                              return (
                                <Checkbox
                                  checked={record.selected || record.cash_amount > 0}
                                  onChange={(e) => toggleRowSelected(index, e.target.checked)}
                                />
                              );
                            },
                          },
                          {
                            title: t('invoice'),
                            dataIndex: 'invoice_number',
                            render: (text: string, record: SettlementRow) => (
                              <InvoiceNumberWithNarration
                                number={text}
                                narration={record.narration}
                                extra={record.invoice_type === 'DirectExpense' ? <Tag color="blue">{t('directPi')}</Tag> : null}
                              />
                            ),
                          },
                          { title: t('due'), dataIndex: 'due_date', width: 100 },
                          {
                            title: t('outstanding'),
                            dataIndex: 'balance_due',
                            align: 'right' as const,
                            render: (v: number) => `${currency} ${Number(v).toFixed(2)}`,
                          },
                          {
                            title: t('cashAllocate'),
                            dataIndex: 'cash_amount',
                            align: 'right' as const,
                            width: 160,
                            render: (v: number, record: SettlementRow) => {
                              const index = settlementRows.findIndex(
                                (x) => x.supplier_invoice_id === record.supplier_invoice_id
                              );
                              return (
                                <Space size="small">
                                  <InputNumber
                                    min={0}
                                    max={record.balance_due}
                                    value={v}
                                    onChange={(val) => setCashAmount(index, Number(val) || 0)}
                                    style={{ width: 100 }}
                                  />
                                  <Button type="link" size="small" onClick={() => payFullOutstanding(index)}>
                                    {tc('full')}
                                  </Button>
                                </Space>
                              );
                            },
                          },
                        ]}
                      />
                    )}
                  </Card>
                );
              })}
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {t('totalCashAllocated', { amount: settlementSummary.totalCash.toFixed(2) })}
              </Typography.Text>
            </Card>
            <Card title={t('adjustmentsSummary')} style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="discount_amount" label={t('discount')}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="write_off_amount" label={t('writeOff')}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="settlement_remarks" label={t('settlementRemarks')}>
                    <TextArea rows={2} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </>
        )}

        {paymentType === 'SupplierBillPayment' && !isMultiSupplier && supplierId && currency && (
          <>
            <Card title={t('allocationAgainstSupplierInvoices')} style={{ marginBottom: 16 }} loading={apContextLoading}>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                {t('allocationAgainstHint', { currency })}
              </Typography.Paragraph>
              {!apContextLoading && !settlementRows.length && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={t('noOpenInvoicesForSupplier', { currency })}
                />
              )}
              {!apContextLoading && !!apContext?.awaiting_posting?.length && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={t('invoicesAwaitingPosting', { count: apContext.awaiting_posting.length })}
                  description={
                    <Space direction="vertical" size={2}>
                      {apContext.awaiting_posting.map((inv) => (
                        <Typography.Link
                          key={inv.id}
                          onClick={() =>
                            navigate(
                              inv.is_direct
                                ? `/fin/direct-purchase-invoices/${inv.id}`
                                : `/scm/procurement/supplier-invoices/${inv.id}`
                            )
                          }
                        >
                          {inv.invoice_number}
                          {inv.supplier_invoice_number ? ` (${inv.supplier_invoice_number})` : ''} —{' '}
                          {inv.is_direct ? t('directPurchaseInvoice') : t('supplierInvoice')} · {inv.invoice_status}
                          {inv.approval_status && inv.approval_status !== inv.invoice_status ? ` / ${inv.approval_status}` : ''} ·{' '}
                          {inv.currency} {inv.grand_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography.Link>
                      ))}
                    </Space>
                  }
                />
              )}
              <Table
                size="small"
                rowKey="supplier_invoice_id"
                dataSource={settlementRows}
                pagination={false}
                rowClassName={(r) => (r.days_overdue > 0 ? 'pv-settlement-overdue' : '')}
                columns={[
                  {
                    title: '',
                    key: 'select',
                    width: 48,
                    render: (_: unknown, record: SettlementRow, index: number) => (
                      <Checkbox
                        checked={record.selected || record.cash_amount > 0}
                        onChange={(e) => toggleRowSelected(index, e.target.checked)}
                      />
                    ),
                  },
                  {
                    title: t('invoice'),
                    dataIndex: 'invoice_number',
                    render: (text: string, record: SettlementRow) => (
                      <InvoiceNumberWithNarration
                        number={text}
                        narration={record.narration}
                        extra={record.invoice_type === 'DirectExpense' ? <Tag color="blue">{t('directPi')}</Tag> : null}
                      />
                    ),
                  },
                  { title: t('due'), dataIndex: 'due_date', width: 100 },
                  {
                    title: t('age'),
                    dataIndex: 'days_overdue',
                    width: 72,
                    render: (d: number) =>
                      d > 0 ? <Tag color="red">{t('daysOverdue', { days: d })}</Tag> : <Tag>{t('current')}</Tag>,
                  },
                  {
                    title: t('currShort'),
                    key: 'currency',
                    width: 56,
                    render: () => currency ?? '—',
                  },
                  {
                    title: t('outstanding'),
                    dataIndex: 'balance_due',
                    align: 'right',
                    render: (v: number) => `${currency ?? ''} ${Number(v).toFixed(2)}`.trim(),
                  },
                  {
                    title: t('cashAllocate'),
                    dataIndex: 'cash_amount',
                    align: 'right',
                    width: 150,
                    render: (v: number, record: SettlementRow, index: number) => (
                      <Space size="small">
                        <InputNumber
                          min={0}
                          max={record.balance_due}
                          value={v}
                          onChange={(val) => setCashAmount(index, Number(val) || 0)}
                          style={{ width: 100 }}
                        />
                        <Button type="link" size="small" onClick={() => payFullOutstanding(index)}>
                          {tc('full')}
                        </Button>
                      </Space>
                    ),
                  },
                  {
                    title: t('advance'),
                    key: 'advance',
                    align: 'right',
                    width: 90,
                    render: (_: unknown, record: SettlementRow) =>
                      rowAdvanceTotal(record.supplier_invoice_id).toFixed(2),
                  },
                  {
                    title: t('debitNote'),
                    key: 'dn',
                    align: 'right',
                    width: 90,
                    render: (_: unknown, record: SettlementRow) =>
                      rowDnTotal(record.supplier_invoice_id).toFixed(2),
                  },
                  {
                    title: t('lineTotal'),
                    key: 'line_total',
                    align: 'right',
                    width: 100,
                    render: (_: unknown, record: SettlementRow) =>
                      (
                        record.cash_amount +
                        rowAdvanceTotal(record.supplier_invoice_id) +
                        rowDnTotal(record.supplier_invoice_id)
                      ).toFixed(2),
                  },
                ]}
              />
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {t('totalCashAndCredits', { cash: settlementSummary.totalCash.toFixed(2), credits: (settlementSummary.totalCash + settlementSummary.totalAdv + settlementSummary.totalDn).toFixed(2) })}
              </Typography.Text>
            </Card>
            {!!apContext?.advances?.length && (
              <Card title={t('advanceAdjustments')} style={{ marginBottom: 16 }}>
                {apContext.advances.map((adv) => (
                  <div key={adv.id}>
                    <Typography.Text strong>{adv.voucher_number}</Typography.Text>
                    <Typography.Text type="secondary"> {t('openBalance', { amount: Number(adv.open_balance).toFixed(2) })}</Typography.Text>
                    {settlementRows.map((row) => (
                      <Row key={`${row.supplier_invoice_id}-${adv.id}`} gutter={8} align="middle" style={{ marginTop: 8 }}>
                        <Col span={10}>
                          <InvoiceNumberWithNarration number={row.invoice_number} narration={row.narration} />
                        </Col>
                        <Col span={8}>
                          <InputNumber
                            min={0}
                            max={adv.open_balance}
                            style={{ width: '100%' }}
                            value={advanceAdj[`${row.supplier_invoice_id}:${adv.id}`] ?? 0}
                            onChange={(val) =>
                              setAdvanceAdj((prev) => ({
                                ...prev,
                                [`${row.supplier_invoice_id}:${adv.id}`]: Number(val) || 0,
                              }))
                            }
                          />
                        </Col>
                      </Row>
                    ))}
                  </div>
                ))}
              </Card>
            )}
            {!!apContext?.debit_notes?.length && (
              <Card title={t('debitNoteAdjustments')} style={{ marginBottom: 16 }}>
                {apContext.debit_notes.map((dn) => (
                  <div key={dn.id}>
                    <Typography.Text strong>{dn.debit_note_number}</Typography.Text>
                    <Typography.Text type="secondary"> {t('openBalance', { amount: Number(dn.open_balance).toFixed(2) })}</Typography.Text>
                    {settlementRows.map((row) => (
                      <Row key={`${row.supplier_invoice_id}-${dn.id}`} gutter={8} align="middle" style={{ marginTop: 8 }}>
                        <Col span={10}>
                          <InvoiceNumberWithNarration number={row.invoice_number} narration={row.narration} />
                        </Col>
                        <Col span={8}>
                          <InputNumber
                            min={0}
                            max={dn.open_balance}
                            style={{ width: '100%' }}
                            value={dnAdj[`${row.supplier_invoice_id}:${dn.id}`] ?? 0}
                            onChange={(val) =>
                              setDnAdj((prev) => ({
                                ...prev,
                                [`${row.supplier_invoice_id}:${dn.id}`]: Number(val) || 0,
                              }))
                            }
                          />
                        </Col>
                      </Row>
                    ))}
                  </div>
                ))}
              </Card>
            )}
            <Card title={t('adjustmentsSummary')} style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="discount_amount" label={t('discount')}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="write_off_amount" label={t('writeOff')}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="settlement_remarks" label={t('settlementRemarks')}>
                    <TextArea rows={2} />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
              <Row gutter={16}>
                <Col span={12}>
                  <Typography.Text>{t('cashPayable', { amount: settlementSummary.totalCash.toFixed(2) })}</Typography.Text>
                  <br />
                  <Typography.Text>{t('minusAdvances', { amount: settlementSummary.totalAdv.toFixed(2) })}</Typography.Text>
                  <br />
                  <Typography.Text>{t('minusDebitNotes', { amount: settlementSummary.totalDn.toFixed(2) })}</Typography.Text>
                  <br />
                  <Typography.Text>{t('minusDiscount', { amount: settlementSummary.discount.toFixed(2) })}</Typography.Text>
                  <br />
                  <Typography.Text>{t('minusWriteOff', { amount: settlementSummary.writeOff.toFixed(2) })}</Typography.Text>
                </Col>
                <Col span={12}>
                  <Alert
                    type="info"
                    message={t('netSettlementCredits', { amount: (settlementSummary.totalAdv + settlementSummary.totalDn + settlementSummary.discount + settlementSummary.writeOff).toFixed(2) })}
                    description={t('headerAmountSyncsCash')}
                  />
                </Col>
              </Row>
            </Card>
          </>
        )}

        <Card title={t('additionalCharges')} style={{ marginBottom: 16 }}>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {t('additionalChargesHint')}
          </Typography.Paragraph>
          <Button type="dashed" onClick={addChargeLine} style={{ marginBottom: 8 }}>
            {t('addChargeLine')}
          </Button>
          <Table
            size="small"
            rowKey={(_, i) => `charge-${i}`}
            dataSource={chargeLines}
            pagination={false}
            columns={[
              {
                title: t('account'),
                dataIndex: 'account_id',
                render: (_: unknown, record: any, index: number) => (
                  <Select
                    style={{ width: 280 }}
                    placeholder={t('selectAccount')}
                    showSearch
                    optionFilterProp="label"
                    value={record.account_id || undefined}
                    onChange={(v) => updateChargeLine(index, 'account_id', v)}
                    options={directLineAccountOptions}
                  />
                ),
              },
              {
                title: t('project'),
                dataIndex: 'project_id',
                render: (_: unknown, record: any, index: number) => (
                  <Select
                    style={{ width: 220 }}
                    placeholder={t('selectProject')}
                    showSearch
                    optionFilterProp="label"
                    value={record.project_id || undefined}
                    onChange={(v) => updateChargeLine(index, 'project_id', v)}
                    options={projectOptions}
                  />
                ),
              },
              {
                title: t('amount'),
                dataIndex: 'amount',
                render: (_: unknown, record: any, index: number) => (
                  <InputNumber
                    min={0}
                    value={record.amount}
                    onChange={(v) => updateChargeLine(index, 'amount', Number(v) || 0)}
                    style={{ width: 120 }}
                    addonAfter={currency || undefined}
                  />
                ),
              },
              {
                title: t('description'),
                dataIndex: 'description',
                render: (_: unknown, record: any, index: number) => (
                  <Input value={record.description} onChange={(e) => updateChargeLine(index, 'description', e.target.value)} />
                ),
              },
              {
                title: '',
                render: (_: unknown, __: any, index: number) => (
                  <Button type="link" danger size="small" onClick={() => removeChargeLine(index)}>
                    {tc('remove')}
                  </Button>
                ),
              },
            ]}
          />
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            {t('principalChargesOutflow', { currency: currency ?? '', principal: principalAmount.toFixed(2), charges: chargesTotal.toFixed(2), total: totalBankOutflow.toFixed(2) })}
          </Typography.Text>
        </Card>

        {(paymentType === 'DirectPayment' || paymentType === 'ExpensePayment') && (
          <Card title={t('directLines')} style={{ marginBottom: 16 }}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {t('directLinesHint')}
            </Typography.Paragraph>
            <Button type="dashed" onClick={addDirectLine} style={{ marginBottom: 8 }}>
              {t('addLine')}
            </Button>
            <Table
              size="small"
              rowKey={(_, i) => String(i)}
              dataSource={directLines}
              pagination={false}
              columns={[
                {
                  title: t('account'),
                  dataIndex: 'account_id',
                  render: (_: unknown, record: any, index: number) => (
                    <Select
                      style={{ width: 280 }}
                      placeholder={t('selectAccount')}
                      showSearch
                      optionFilterProp="label"
                      value={record.account_id || undefined}
                      onChange={(v) => updateDirectLine(index, 'account_id', v)}
                      options={directLineAccountOptions}
                    />
                  ),
                },
                {
                  title: t('project'),
                  dataIndex: 'project_id',
                  render: (_: unknown, record: any, index: number) => (
                    <Select
                      style={{ width: 220 }}
                      placeholder={t('selectProject')}
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      value={record.project_id || undefined}
                      onChange={(v) => updateDirectLine(index, 'project_id', v)}
                      options={projectOptions}
                    />
                  ),
                },
                {
                  title: t('amount'),
                  dataIndex: 'amount',
                  render: (_: unknown, record: any, index: number) => (
                    <InputNumber min={0} value={record.amount} onChange={(v) => updateDirectLine(index, 'amount', Number(v) || 0)} style={{ width: 120 }} />
                  ),
                },
                {
                  title: t('description'),
                  dataIndex: 'description',
                  render: (_: unknown, record: any, index: number) => (
                    <Input value={record.description} onChange={(e) => updateDirectLine(index, 'description', e.target.value)} />
                  ),
                },
                {
                  title: '',
                  render: (_: unknown, __: any, index: number) => (
                    <Button type="link" danger size="small" onClick={() => removeDirectLine(index)}>
                      {tc('remove')}
                    </Button>
                  ),
                },
              ]}
            />
            <Typography.Text type="secondary">{t('total')}: {directLines.reduce((sum, l) => sum + l.amount, 0).toFixed(2)}</Typography.Text>
          </Card>
        )}
      </Form>
      {isEdit && id ? (
        <ProcurementAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_PAYMENT_VOUCHER}
          entityId={id}
          title={tc('attachments')}
          readOnly={pv?.status === 'Cancelled'}
          queryKeyPrefix={['documents', 'payment-voucher']}
        />
      ) : null}
    </div>
  );
};

export default PaymentVoucherFormPage;
